import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { X, User, AlertCircle, Plus, Check } from 'lucide-react';
import clsx from 'clsx';
import SearchInput from './SearchInput';
import Button from './Button';
import Badge from './Badge';
import CloseButton from './CloseButton';
import ModalLoadingOverlay from './ModalLoadingOverlay';
import Message from './Message';
import { userService } from '@services';
import { ApiError } from '@app-types';
import type { User as AppUser } from '@app-types';
import { formatPersonName } from '@utils/stringUtils';
import { useDebounce } from '@hooks/useDebounce';
import { LoadingState } from '@components/common/loading';
import styles from './MultiRoleAssignmentModal.module.css';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const AssigneeDetails: React.FC<{ user: AppUser; dataTestId: string }> = ({ user, dataTestId }) => (
  <div className={styles.userInfo}>
    <span className={styles.userName}>{formatPersonName(user)}</span>
    <dl className={styles.userMeta}>
      <dt>Division</dt>
      <dd>
        {user.division_name ? (
          <span className={styles.userDivisionBadge} data-test-id={`${dataTestId}-user-division`}>
            {user.division_name}
          </span>
        ) : (
          <span className={styles.noDivision}>No division</span>
        )}
      </dd>
      <dt>Status</dt>
      <dd>
        <Badge className={styles.statusBadge} variant={user.is_active ? 'success' : 'error'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </dd>
      <dt>Email</dt>
      <dd className={styles.userEmail} title={user.email}>
        {user.email}
      </dd>
    </dl>
  </div>
);

export interface MultiRoleAssignmentModalProps {
  contractNumber: string;
  supplierName?: string;
  roleType: 'owner' | 'manager';
  currentAssigneeIds: number[];
  onClose: () => void;
  onAssigned: (assigneeIds: number[]) => Promise<void>;
  dataTestId?: string;
}

const MultiRoleAssignmentModal: React.FC<MultiRoleAssignmentModalProps> = ({
  contractNumber,
  supplierName,
  roleType,
  currentAssigneeIds,
  onClose,
  onAssigned,
  dataTestId = 'multi-assignment'
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(currentAssigneeIds));
  // Rows of users added from the Available list, kept so they stay in Currently Selected after the search changes
  const [addedUsers, setAddedUsers] = useState<Map<number, AppUser>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm.trim(), SEARCH_DEBOUNCE_MS);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReassignment = currentAssigneeIds.length > 0;

  // Get role-specific configuration
  const getRoleConfig = () => {
    switch (roleType) {
      case 'manager':
        return {
          fieldLabel: 'Contract Manager',
          title: isReassignment ? 'Reassign Contract Manager' : 'Assign Contract Manager',
          description:
            'You are about to assign or reassign the Contract Manager. This person will oversee the contract management process. You can assign multiple managers.',
          important: 'IMPORTANT: Contract Managers will be assigned to the contract.',
          buttonText: 'Apply Changes'
        };
      case 'owner':
        return {
          fieldLabel: 'Contract Owner',
          title: isReassignment ? 'Reassign Contract Owner' : 'Assign Contract Owner',
          description:
            'You are about to assign or reassign the Contract Owner. This person will be the primary owner of this contract. You can assign multiple owners, but they must be from the same division to avoid putting the contract on hold.',
          important: 'IMPORTANT: Contract Owners will be assigned to the contract',
          buttonText: 'Apply Changes'
        };
      default:
        throw new Error(`Unknown role type: ${roleType}`);
    }
  };

  const config = getRoleConfig();
  const roleLabelPlural = `${config.fieldLabel.toLowerCase()}s`;

  // Only active users are offered; the server searches and pages them
  const {
    data: availablePages,
    isLoading: loadingUsers,
    isError: usersFailed,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['users', 'contract-assignee-search', debouncedSearch],
    queryFn: ({ pageParam }) =>
      userService.searchUsers({ ...(debouncedSearch && { search: debouncedSearch }), limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((count, p) => count + p.data.length, 0);
      return lastPage.data.length > 0 && loaded < lastPage.total ? loaded : undefined;
    }
  });

  // Current assignees by id, so an inactive one or one not on a loaded page is still shown and kept
  const {
    data: assignedPage,
    isLoading: loadingAssigned,
    isError: assignedFailed
  } = useQuery({
    queryKey: ['users', 'contract-assignees', currentAssigneeIds],
    queryFn: () => userService.searchUsers({ ids: currentAssigneeIds, limit: currentAssigneeIds.length, offset: 0 }),
    enabled: currentAssigneeIds.length > 0
  });

  const availableUsers = useMemo(() => availablePages?.pages.flatMap((p) => p.data) ?? [], [availablePages]);

  const selectedUsers = useMemo(() => {
    const knownUsers = new Map<number, AppUser>();
    assignedPage?.data.forEach((u) => knownUsers.set(u.id, u));
    addedUsers.forEach((u, id) => knownUsers.set(id, u));
    return Array.from(selectedIds).flatMap((id) => knownUsers.get(id) ?? []);
  }, [assignedPage, addedUsers, selectedIds]);

  const handleToggleUser = (user: AppUser) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(user.id)) {
      newSelected.delete(user.id);
    } else {
      newSelected.add(user.id);
      setAddedUsers((prev) => new Map(prev).set(user.id, user));
    }
    setSelectedIds(newSelected);
  };

  const handleRemoveUser = (userId: number) => {
    const newSelected = new Set(selectedIds);
    newSelected.delete(userId);
    setSelectedIds(newSelected);
  };

  const handleApply = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await onAssigned(Array.from(selectedIds));
      onClose();
    } catch (error) {
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.error || `Failed to assign ${roleType}s`;
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (selectedIds.size !== currentAssigneeIds.length) return true;
    return !currentAssigneeIds.every((id) => selectedIds.has(id));
  }, [selectedIds, currentAssigneeIds]);

  return createPortal(
    <div className={styles.multiAssignmentModalOverlay} data-test-id={`${dataTestId}-modal`}>
      <div className={styles.multiAssignmentModal}>
        {/* Loading Overlay */}
        {isSubmitting && <ModalLoadingOverlay message="Processing request..." />}

        <div className={styles.multiAssignmentHeader}>
          <div className={styles.headerText}>
            <h3 data-test-id={`${dataTestId}-title`}>{config.title}</h3>
          </div>
          <CloseButton onClick={onClose} disabled={isSubmitting} />
        </div>

        <div className={styles.multiAssignmentContent}>
          <div className={styles.contentLayout}>
            {/* Left Panel - Description & Contract Info */}
            <div className={styles.leftPanel}>
              <p className={styles.description}>{config.description}</p>
              <p className={styles.important}>{config.important}</p>
              <div className={styles.contractInfoBox}>
                <h4>{contractNumber}</h4>
                {supplierName && <p className={styles.supplierName}>{supplierName}</p>}
              </div>

              {/* Currently Selected Users */}
              <div className={styles.selectedSection}>
                <h5>Currently Selected {config.fieldLabel}(s):</h5>
                {loadingAssigned && <LoadingState message={`Loading current ${roleLabelPlural}...`} />}
                {assignedFailed && (
                  <Message variant="error" icon={<AlertCircle size={16} />}>
                    Failed to load current {roleLabelPlural}
                  </Message>
                )}
                {selectedUsers.length > 0 && (
                  <div className={styles.selectedUsersList} data-test-id={`${dataTestId}-selected-list`}>
                    {selectedUsers.map((user) => (
                      <div key={user.id} className={styles.selectedUserCard} data-test-id={`${dataTestId}-user`}>
                        <div className={styles.userIcon}>
                          <User size={16} />
                        </div>
                        <AssigneeDetails user={user} dataTestId={dataTestId} />
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={isSubmitting}
                          title="Remove"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {!loadingAssigned && !assignedFailed && selectedUsers.length === 0 && (
                  <p className={styles.noSelection}>No {roleLabelPlural} selected</p>
                )}
              </div>
            </div>

            {/* Right Panel - Available Users */}
            <div className={styles.rightPanel}>
              <h5>Available {config.fieldLabel}s</h5>

              {/* Search Bar */}
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by name, email, or division..."
                disabled={isSubmitting}
              />

              {/* Available Users List */}
              {loadingUsers && <LoadingState message={`Loading ${roleLabelPlural}...`} />}
              {usersFailed && (
                <Message variant="error" icon={<AlertCircle size={16} />}>
                  Failed to load {roleLabelPlural}
                </Message>
              )}
              {availableUsers.length > 0 && (
                <div className={styles.availableUsersList} data-test-id={`${dataTestId}-available-list`}>
                  {availableUsers.map((user) => {
                    const isSelected = selectedIds.has(user.id);
                    const isNew = isSelected && !currentAssigneeIds.includes(user.id);

                    return (
                      <div
                        key={user.id}
                        className={clsx(styles.availableUserCard, isSelected && styles.selected)}
                        data-test-id={`${dataTestId}-user`}
                      >
                        <div className={styles.userIcon}>
                          <User size={16} />
                        </div>
                        <AssigneeDetails user={user} dataTestId={dataTestId} />
                        <div className={styles.userActions}>
                          {isNew && <span className={styles.addedBadge}>Added</span>}
                          <button
                            type="button"
                            className={clsx(styles.addButton, isSelected && styles.added)}
                            onClick={() => handleToggleUser(user)}
                            disabled={isSubmitting}
                            title={isSelected ? 'Remove' : 'Add'}
                          >
                            {isSelected ? <Check size={16} /> : <Plus size={16} />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {hasNextPage && (
                <Button
                  variant="secondary"
                  size="sm"
                  className={styles.loadMoreButton}
                  onClick={() => fetchNextPage()}
                  loading={isFetchingNextPage}
                  disabled={isSubmitting}
                  dataTestId={`${dataTestId}-load-more`}
                >
                  Load more
                </Button>
              )}
              {!loadingUsers && !usersFailed && availableUsers.length === 0 && (
                <div className="empty-state">
                  <User size={48} className="icon-muted" />
                  <p>No {roleLabelPlural} found</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <Message variant="error" icon={<AlertCircle size={16} />}>
              {error}
            </Message>
          )}
        </div>

        <div className={styles.multiAssignmentFooter}>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>

          <Button variant="primary" onClick={handleApply} disabled={!hasChanges || isSubmitting}>
            {isSubmitting ? 'Applying Changes...' : config.buttonText}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MultiRoleAssignmentModal;
